const ORGANIZATION_TYPES = ["LEGAL_ENTITY", "FUNCTION"];
const LOCATION_TYPES = [
  "FIXED_PHYSICAL_LOCATION",
  "MOBILE_PHYSICAL_LOCATION",
  "DIGITAL_LOCATION",
];

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function validGln(value) {
  if (!/^\d{13}$/.test(String(value || ""))) return false;

  const digits = String(value).split("").map(Number);
  const checkDigit = digits.pop();
  const sum = digits.reduce(
    (total, digit, index) => total + digit * (index % 2 === 0 ? 1 : 3),
    0
  );

  return (10 - (sum % 10)) % 10 === checkDigit;
}

export function localizedFields(value) {
  return {
    ka: (value && value.ka) || "",
    en: (value && value.en) || "",
  };
}

export function isOrganizationGlnType(glnType) {
  return ORGANIZATION_TYPES.includes(glnType);
}

export function isLocationGlnType(glnType) {
  return LOCATION_TYPES.includes(glnType);
}

function identifierRows(rows, field) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => {
    const identifier = {};
    identifier[field] = (row && row[field]) || "";
    identifier[field + "_type"] = (row && row[field + "_type"]) || null;
    return identifier;
  });
}

function relationshipRows(rows) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    gln: (row && row.gln) || "",
    gln_type: (row && row.gln_type) || null,
  }));
}

export function defaultGlnOptionalFields() {
  return {
    organization_role: [],
    location_role: [],
    additional_organization_id: [],
    additional_location_id: [],
    parent_organization_gln: null,
    parent_organization_gln_type: null,
    contained_in_place_gln: null,
    related_organization: [],
    location: [],
    address_country_code: null,
    address_locality: { ka: "", en: "" },
    address_postal_name: { ka: "", en: "" },
    base_location_gln: null,
  };
}

export function populateGlnOptionalFields(data) {
  return {
    organization_role: Array.isArray(data.organization_role)
      ? data.organization_role.slice()
      : [],
    location_role: Array.isArray(data.location_role)
      ? data.location_role.slice()
      : [],
    additional_organization_id: identifierRows(
      data.additional_organization_id,
      "organization_id"
    ),
    additional_location_id: identifierRows(
      data.additional_location_id,
      "location_id"
    ),
    parent_organization_gln: data.parent_organization_gln || null,
    parent_organization_gln_type: data.parent_organization_gln_type || null,
    contained_in_place_gln: data.contained_in_place_gln || null,
    related_organization: relationshipRows(data.related_organization),
    location: relationshipRows(data.location),
    address_country_code: data.address_country_code || null,
    address_locality: localizedFields(data.address_locality),
    address_postal_name: localizedFields(data.address_postal_name),
    base_location_gln: data.base_location_gln || null,
  };
}

export function resetInapplicableGlnOptionalFields(item, glnType) {
  const isOrganization = isOrganizationGlnType(glnType);
  const isLocation = isLocationGlnType(glnType);

  if (isOrganization) {
    item.location_role = [];
    item.additional_location_id = [];
    item.related_organization = [];
    item.contained_in_place_gln = null;
    item.base_location_gln = null;
  } else if (isLocation) {
    item.organization_role = [];
    item.additional_organization_id = [];
    item.parent_organization_gln = null;
    item.parent_organization_gln_type = null;
    item.location = [];
    item.address_country_code = null;
    item.address_locality = { ka: "", en: "" };
    item.address_postal_name = { ka: "", en: "" };

    if (glnType !== "FIXED_PHYSICAL_LOCATION") {
      item.contained_in_place_gln = null;
    }
    if (glnType !== "MOBILE_PHYSICAL_LOCATION") {
      item.base_location_gln = null;
    }
  }
}

export function validateGlnOptionalFields(item, glnType, enumOptions, message) {
  const errors = {};
  const isOrganization = isOrganizationGlnType(glnType);
  const isLocation = isLocationGlnType(glnType);
  const validEnum = (category, value) =>
    enumOptions(category).some((option) => option.value === value);
  const addressStarted = [
    item.address_country_code,
    item.address_locality.ka,
    item.address_locality.en,
    item.address_postal_name.ka,
    item.address_postal_name.en,
  ].some(hasValue);

  if (isOrganization) {
    if (
      item.organization_role.some(
        (value) => !validEnum("organization_role", value)
      )
    ) {
      errors.organization_role = message;
    }

    item.additional_organization_id.forEach((row, index) => {
      if (
        !hasValue(row.organization_id) ||
        !row.organization_id_type ||
        !validEnum("additional_organization_id_type", row.organization_id_type)
      ) {
        errors["additional_organization_id_" + index] = message;
      }
    });

    const hasParentGln = hasValue(item.parent_organization_gln);
    const hasParentType = hasValue(item.parent_organization_gln_type);
    if (
      hasParentGln !== hasParentType ||
      (hasParentType &&
        !validEnum(
          "parent_organization_gln_type",
          item.parent_organization_gln_type
        ))
    ) {
      errors.parent_organization_gln_type = message;
    }
    if (hasParentGln && !validGln(item.parent_organization_gln)) {
      errors.parent_organization_gln = message;
    }

    if (addressStarted) {
      if (
        !item.address_country_code ||
        !validEnum("address_country_code", item.address_country_code)
      ) {
        errors.address_country_code = message;
      }

      const kaLocality = hasValue(item.address_locality.ka);
      const kaPostal = hasValue(item.address_postal_name.ka);
      const enLocality = hasValue(item.address_locality.en);
      const enPostal = hasValue(item.address_postal_name.en);
      if (
        kaLocality !== kaPostal ||
        enLocality !== enPostal ||
        (!(kaLocality && kaPostal) && !(enLocality && enPostal))
      ) {
        errors.address_ka = message;
        errors.address_en = message;
      }
    }
  }

  if (isLocation) {
    if (
      item.location_role.some((value) => !validEnum("location_role", value))
    ) {
      errors.location_role = message;
    }

    item.additional_location_id.forEach((row, index) => {
      if (
        !hasValue(row.location_id) ||
        row.location_id.length > 100 ||
        !row.location_id_type ||
        !validEnum("additional_location_id_type", row.location_id_type)
      ) {
        errors["additional_location_id_" + index] = message;
      }
    });
  }

  const relationshipFields = [
    {
      field: "location",
      isApplicable: isOrganization,
      typeCategory: "location_gln_type",
    },
    {
      field: "related_organization",
      isApplicable: isLocation,
      typeCategory: "related_organization_gln_type",
    },
  ];

  relationshipFields.forEach(({ field, isApplicable, typeCategory }) => {
    if (!isApplicable) return;

    const rows = item[field];
    if (rows.length > 100) errors[field] = message;

    const seen = new Set();
    rows.forEach((row, index) => {
      if (
        !validGln(row.gln) ||
        !row.gln_type ||
        !validEnum(typeCategory, row.gln_type)
      ) {
        errors[field + "_" + index] = message;
      }

      const key = row.gln + "|" + row.gln_type;
      if (row.gln && row.gln_type && seen.has(key)) {
        errors[field + "_" + index] = message;
      }
      seen.add(key);
    });
  });

  if (
    glnType === "FIXED_PHYSICAL_LOCATION" &&
    hasValue(item.contained_in_place_gln) &&
    !validGln(item.contained_in_place_gln)
  ) {
    errors.contained_in_place_gln = message;
  }
  if (
    glnType === "MOBILE_PHYSICAL_LOCATION" &&
    hasValue(item.base_location_gln) &&
    !validGln(item.base_location_gln)
  ) {
    errors.base_location_gln = message;
  }

  return errors;
}

export function buildGlnOptionalPayload(item, glnType) {
  const isOrganization = isOrganizationGlnType(glnType);
  const isLocation = isLocationGlnType(glnType);

  return {
    organization_role: isOrganization ? item.organization_role.slice() : [],
    location_role: isLocation ? item.location_role.slice() : [],
    additional_organization_id: isOrganization
      ? identifierRows(item.additional_organization_id, "organization_id")
      : [],
    additional_location_id: isLocation
      ? identifierRows(item.additional_location_id, "location_id")
      : [],
    parent_organization_gln: isOrganization
      ? item.parent_organization_gln || null
      : null,
    parent_organization_gln_type: isOrganization
      ? item.parent_organization_gln_type || null
      : null,
    contained_in_place_gln:
      glnType === "FIXED_PHYSICAL_LOCATION"
        ? item.contained_in_place_gln || null
        : null,
    related_organization: isLocation
      ? relationshipRows(item.related_organization)
      : [],
    location: isOrganization ? relationshipRows(item.location) : [],
    address_country_code: isOrganization
      ? item.address_country_code || null
      : null,
    address_locality: isOrganization
      ? localizedFields(item.address_locality)
      : { ka: "", en: "" },
    address_postal_name: isOrganization
      ? localizedFields(item.address_postal_name)
      : { ka: "", en: "" },
    base_location_gln:
      glnType === "MOBILE_PHYSICAL_LOCATION"
        ? item.base_location_gln || null
        : null,
  };
}
